import React from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";
import ImageOrderingEditor, {
  ImageOrderingValue,
} from "../ImageOrderingEditor";
import TagMapEditor from "../TagMapEditor";
import AddTagMapButton from "../AddTagMapButton";
import { suggestionsForMap } from "../standardTags";
import SyndicatorCard from "../SyndicatorCard";

interface Props {
  config: any;
  onChange: (field: string, value: any) => void;
}

const DEFAULT_TAGMAPS = ["exterior_TagMap", "featured_TagMap"];

export const masheryDefaults = {
  syndicatorData: {
    auctionId: "",
    imageSetType: "",
    apiUrl: "",
    appId: "",
    tagMapping: {
      exterior_TagMap: {},
      featured_TagMap: {},
    },
  },
  imageOrdering: {
    ImageOrder: "",
  },
};

const MasheryForm: React.FC<Props> = ({ config, onChange }) => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;

  const syndicatorData = config.syndicatorData || {};
  const tagMapping = syndicatorData.tagMapping || {};
  const tagMapNames = Array.from(
    new Set([
      ...DEFAULT_TAGMAPS,
      ...Object.keys(tagMapping).filter((k) => k.endsWith("_TagMap")),
    ]),
  );
  const imageOrdering: ImageOrderingValue = config.imageOrdering || {};

  return (
    <SyndicatorCard title="Mashery (Manheim) Configuration">
      <Stack spacing={3}>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Syndicator Data
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Auction ID"
                fullWidth
                size="small"
                value={syndicatorData.auctionId || ""}
                onChange={(e) =>
                  onChange("syndicatorData.auctionId", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="App ID"
                fullWidth
                size="small"
                value={syndicatorData.appId || ""}
                onChange={(e) =>
                  onChange("syndicatorData.appId", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Image Set Type"
                fullWidth
                size="small"
                placeholder="CAPTURE"
                value={syndicatorData.imageSetType || ""}
                onChange={(e) =>
                  onChange("syndicatorData.imageSetType", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="API URL"
                fullWidth
                size="small"
                value={syndicatorData.apiUrl || ""}
                onChange={(e) =>
                  onChange("syndicatorData.apiUrl", e.target.value)
                }
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Tag Mapping
          </Typography>
          <Stack spacing={2}>
            {tagMapNames.map((mapName) => {
              const isDefault = DEFAULT_TAGMAPS.includes(mapName);
              return (
                <TagMapEditor
                  key={mapName}
                  label={mapName}
                  value={tagMapping[mapName]}
                  onChange={(next) =>
                    onChange(`syndicatorData.tagMapping.${mapName}`, next)
                  }
                  onRemove={
                    isDefault
                      ? undefined
                      : () => {
                          const next = { ...tagMapping };
                          delete next[mapName];
                          onChange("syndicatorData.tagMapping", next);
                        }
                  }
                  keyLabel="Tag"
                  valueLabel="Mashery Code"
                  tagSuggestions={suggestionsForMap(mapName)}
                />
              );
            })}
            <AddTagMapButton
              existingNames={tagMapNames}
              onAdd={(name) =>
                onChange(`syndicatorData.tagMapping.${name}`, {})
              }
            />
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, color: accent, fontWeight: 600 }}
          >
            Image Ordering
          </Typography>
          <ImageOrderingEditor
            value={imageOrdering}
            onChange={(next) => onChange("imageOrdering", next)}
          />
        </Box>
      </Stack>
    </SyndicatorCard>
  );
};

export default MasheryForm;
